/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory } from 'react-router';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ROUTES } from '../../constants/routes';
import { generateUrl } from '../../routes/util';
import {
  AssuranceDocumentType,
  AssuranceListPortfolio,
  AssuranceStatus,
  Organization
} from '../../types/assurance';
import { DATE, formatDate, formatDateUTC } from '../../utils/date';
import Dashboard, { DashboardRow, DashboardSection } from '../dashboard';
import Loader from '../loader';
import { TrackingList, Column, Row, Divider } from '../tracking-list';
import G17Client from '../../services/G17Client';
import { BasicAlert } from '../alert/BasicAlert';
import SimpleTooltip from '../simple-tooltip';
import { getAssuranceDocName, NO_ACCESS_MESSAGE, statusColumns } from '../../constants/assurance';
import { downloadPortfolioBundle } from '../../actions/assurance';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { naturalSort } from '../../utils';
import './assurance-list.scss';
import { useAppSelector } from '../../reducers';
import { getCurrentUser } from '../../selectors/user';
import { ManageOrganizationMembers } from './stakeholders/ManageOrganizationMembers';
import { CurrentUserData } from '../../reducers/current-user';
import { getFullName } from '../../utils/user';
import { UtrvStatus } from '../../constants/status';

const getAssurerName = (assurancePortfolio: AssuranceListPortfolio) => {
  if (!assurancePortfolio.assurers) {
    return '';
  }
  const firstAdmin = assurancePortfolio.assurers.find(user => user.isAdmin);

  if (!firstAdmin) {
    return '';
  }

  return getFullName(firstAdmin, '-');
};

const hasPermission = (assurancePortfolio: AssuranceListPortfolio, currentUser: CurrentUserData) => {
  return (
    assurancePortfolio.assurers &&
    assurancePortfolio.assurers.some((assurer) => assurer._id === currentUser._id)
  );
};

const getStatusList = (assurancePortfolio: AssuranceListPortfolio) => {
  const isCompleted = assurancePortfolio.universalTrackerValueAssurances.filter(
    (utrvAssurance) => utrvAssurance.status === AssuranceStatus.Completed
  ).map(utrvAssurance => utrvAssurance.utrvId);
  const isRejected = assurancePortfolio.universalTrackerValueAssurances.filter(
    (utrvAssurance) => utrvAssurance.status === AssuranceStatus.Rejected
  ).map(utrvAssurance => utrvAssurance.utrvId);
  const questionStatuses = assurancePortfolio.universalTrackerValues.reduce((accumulator, utrv) => {
    if (isCompleted.includes(utrv._id)) {
      return accumulator;
    }
    return {
      notReady: utrv.status !== UtrvStatus.Verified ? [...accumulator.notReady, utrv._id] : accumulator.notReady,
      isVerified: utrv.status === UtrvStatus.Verified ? [...accumulator.isVerified, utrv._id] : accumulator.isVerified,
    }
  }, { notReady: [] as string[], isVerified: [] as string[] });
  return {
    ...questionStatuses,
    isCompleted,
    isRejected,
  }
};

const AssuranceList = () => {
  const currentUser = useAppSelector(getCurrentUser);
  const [assuranceList, setAssuranceList] = React.useState<AssuranceListPortfolio[]>();
  const [message, setMessage] = React.useState('');
  const history = useHistory();
  const redirect = (assurancePortfolioId: string) => {
    history.push(generateUrl(ROUTES.ASSURANCE_PORTFOLIO, { assurancePortfolioId }));
  }
  const [organization, setOrganization] =
    React.useState<Pick<Organization, '_id' | 'partnerTypes' | 'name' | 'organizationType'>>();
  const [isOrganizationMembersOpen, setOrganizationMembersOpen] = React.useState(false);
  const toggle = () => setOrganizationMembersOpen((isOpen) => !isOpen);

  React.useEffect(() => {
    G17Client.getAssurerPortfolios().then(setAssuranceList).catch(e => {
      setMessage(e.message);
      setAssuranceList([])
    });
  }, []);

  React.useEffect(() => {
    if (!currentUser) return;
    G17Client.getUserOrganization().then(setOrganization).catch(e => {
      setMessage(e.message);
      setOrganization(undefined);
    });
  }, [currentUser]);

  const downloadEvidence = (portfolio: AssuranceListPortfolio) => {
    downloadPortfolioBundle(portfolio._id).then(async ([document]) => {
      if (document?.url) {
        window.open(document.url, '_blank', '');
        const analytics = getAnalytics();
        return analytics.track(AnalyticsEvents.AssuranceDataDownloaded, {
          assurancePortolioId: portfolio._id,
          surveyId: portfolio.survey._id,
          initiativeId: portfolio.initiative._id,
          source: 'survey_assurance'
        });
      }
    }).catch((e: Error) => console.log(e))
  }

  function getDocumentBtn(assurancePortfolio: AssuranceListPortfolio, type: AssuranceDocumentType) {
    const file = assurancePortfolio.documents.find(d => d.type === type);
    const tooltip = `${getAssuranceDocName(type)} uploaded`;

    return (
      <SimpleTooltip text={file ? tooltip : 'No document has been uploaded yet'}>
        {file ? <i className='fa fa-file text-ThemeSuccessMedium' /> : <>-</>}
      </SimpleTooltip>
    )
  }

  const getAccessButton = (assurancePortfolio: AssuranceListPortfolio, hasAccess: boolean) => {
    if (hasAccess) {
      return (
        <Button color='link' onClick={() => redirect(assurancePortfolio._id)}>
          {formatDate(assurancePortfolio.survey.effectiveDate, 'MMM', true)}
        </Button>
      );
    }
    return (
      <SimpleTooltip text={NO_ACCESS_MESSAGE}>
        <Button
          color='link'
          disabled
          onClick={() => redirect(assurancePortfolio._id)}
        >
          {formatDate(assurancePortfolio.survey.effectiveDate, 'MMM', true)}
        </Button>
      </SimpleTooltip>
    );
  };

  if (!assuranceList || !organization || !currentUser) {
    return <Loader />;
  }

  const years = new Set();
  assuranceList.forEach(a => {
    years.add(formatDateUTC(a.survey.effectiveDate, DATE.YEAR_ONLY));
  });

  const getDownloadButton = (assurancePortfolio: AssuranceListPortfolio, hasAccess: boolean) => {
    const btn = <Button color='link' disabled={!hasAccess} onClick={() => downloadEvidence(assurancePortfolio)}>
      <i className='fa fa-file-download' />
    </Button>
    return hasAccess ? btn : <SimpleTooltip text={NO_ACCESS_MESSAGE}>{btn}</SimpleTooltip>;
  };

  return (
    <Dashboard className='assurance-list'>
      <>
        <BasicAlert type={'danger'}>{message}</BasicAlert>
        <DashboardRow className='assurance-list__heading'>
          <h1 className='ml-1'>{organization.name}</h1>
          <Button
            className='ml-2 px-3'
            onClick={toggle}
            outline={true}
          >
            View assurance team
            <i className={'fa fa-user ml-2'} />
          </Button>
        </DashboardRow>
        {assuranceList.length === 0
          ?
          <DashboardSection title='Reports'>
            <>There are no reports to be assured.</>
          </DashboardSection>
          :
          Array.from(years).sort().reverse().map(year => (
            <DashboardSection key={year as number} title={`${year} Reports`} >
              <TrackingList border={false}>
                <Row className='header'>
                  <Column className='col_status strong'>Status</Column>
                  <Column className='col_date strong'>Month</Column>
                  <Column className='col_org strong'>Organisation</Column>
                  <Column className='col_assurer strong' fill>Assurer Name</Column>
                  <Column className='col_file' truncateTwoline>Basis of Reporting</Column>
                  <Column className='col_file' truncateTwoline>Management Statement</Column>
                  <Column className='col_file' truncateTwoline>Assurance Statement</Column>
                  {Object.values(statusColumns).map((column) => (
                      <Column key={column.name} className='col_stats'>
                        <SimpleTooltip text={column.text}>
                          <i className={`${column.name} fa-lg`} />
                        </SimpleTooltip>
                      </Column>
                    ))}
                  <Column className='col_download' />
                </Row>
                {assuranceList
                  .filter(d => formatDateUTC(d.survey.effectiveDate, DATE.YEAR_ONLY) === year)
                  .sort((a, b) => naturalSort(b.survey.effectiveDate, a.survey.effectiveDate))
                  .map(d => {
                    const statusList = getStatusList(d);
                    const hasAccess = hasPermission(d, currentUser);
                    return (
                      <Row key={`assurance_row_${d._id}`}>
                        <Column className='col_status' tooltip={d.status === AssuranceStatus.Completed ? 'Assurance Completed' : 'Assurance Not Completed'}>
                          <i className={`fa fa-circle ${d.status === AssuranceStatus.Completed ? 'text-ThemeSuccessMedium' : 'text-ThemeNeutralsLight'}`} />
                        </Column>
                        <Column className='col_date'>
                          {getAccessButton(d, hasAccess)}
                        </Column>
                        <Column stretch truncate className='col_org'>
                          {d.initiative.name}
                        </Column>
                        <Column stretch truncate className='col_assurer'>
                          {getAssurerName(d)}
                        </Column>
                        <Column className='col_file'>
                          {getDocumentBtn(d, 'basis_of_reporting')}
                        </Column>
                        <Column className='col_file'>
                          {getDocumentBtn(d, 'management_statement')}
                        </Column>
                        <Column className='col_file'>
                          {getDocumentBtn(d, 'assurance_statement')}
                        </Column>
                        <Divider />
                        {Object.values(statusColumns).map((column) => (
                          <Column key={column.accessor} className='col_stats'>
                            {statusList[column.accessor as keyof typeof statusList].length || '0'}
                          </Column>
                        ))}
                        <Divider />
                        <Column className='col_download'>
                          {getDownloadButton(d, hasAccess)}
                        </Column>
                      </Row>
                    )
                  })
                }
              </TrackingList>
              <Modal isOpen={isOrganizationMembersOpen} toggle={toggle} backdrop='static' className='modal-lg'>
                <ModalHeader toggle={toggle}>Team members</ModalHeader>
                <ModalBody>
                  <ManageOrganizationMembers organizationId={organization._id} />
                </ModalBody>
              </Modal>
            </DashboardSection>
          ))}
      </>
    </Dashboard>
  );
}
export default AssuranceList;
